import React from 'react';
import { Tag as AntTag } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  AlertOutlined,
  BellOutlined,
} from '@ant-design/icons';
import type { RagStatus } from '@/services/ownerIntelligence.service';
import {
  ragToSignalTier,
  scoreToSignalTier,
  signalTierColor,
  signalTierLabel,
  type SignalTier,
} from './oi-signals';

export function SignalBadge({ tier, score }: { tier?: SignalTier; score?: number }) {
  const resolved = tier ?? (score != null ? scoreToSignalTier(score) : 'watch');
  const color = signalTierColor(resolved);
  const label = signalTierLabel(resolved);

  let icon = <CheckCircleOutlined />;
  if (resolved === 'watch') icon = <WarningOutlined />;
  if (resolved === 'at_risk') icon = <AlertOutlined />;
  if (resolved === 'critical') icon = <BellOutlined />;

  const filled = resolved === 'excellent' || resolved === 'watch' || resolved === 'critical';
  const antColor =
    resolved === 'excellent' || resolved === 'healthy'
      ? 'success'
      : resolved === 'watch'
        ? 'warning'
        : 'error';

  return (
    <AntTag
      color={filled ? antColor : undefined}
      style={
        filled
          ? undefined
          : { borderColor: color, color, background: 'transparent' }
      }
      icon={icon}
    >
      {label}
    </AntTag>
  );
}

/** @deprecated Use SignalBadge */
export function RagTag({ status }: { status: RagStatus }) {
  return <SignalBadge tier={ragToSignalTier(status)} />;
}

export function statusColor(status: RagStatus): string {
  return signalTierColor(ragToSignalTier(status));
}

export function DataCoverageDots({ value, label }: { value: number; label?: string }) {
  const filled = Math.round((value / 100) * 5);
  const title = label ?? `${value}% data coverage`;
  return (
    <span className="oi-confidence-dots" title={title}>
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            marginRight: 4,
            background: i < filled ? (value >= 90 ? 'var(--oi-emerald)' : value >= 75 ? 'var(--oi-amber)' : 'var(--oi-crimson)') : 'var(--oi-border)',
          }}
        />
      ))}
    </span>
  );
}

/** @deprecated Use DataCoverageDots */
export function ConfidenceDots(props: { value: number; label?: string }) {
  return <DataCoverageDots {...props} />;
}

/** @deprecated Use DataCoverageDots */
export function ConfidenceBadge(props: { value: number }) {
  return <DataCoverageDots value={props.value} />;
}
